import { Modal, Table, Typography, Select, Row, Col, Space } from "antd";
import { useQuery } from "react-query";
import { appInject } from "@core/di/utils";
import { DI_TOKENS } from "@shared/constants/di";
import { IBaselinesService } from "@shared/interfaces/baselines-service.interface";
import { BaselineDto } from "@shared/types/baselines/baselines.dto";
import { TierDto } from "@shared/types/tiers/tiers.dto";
import { useState } from "react";

interface ManageBaselinesModalProps {
  open: boolean;
  tier: TierDto | null;
  onClose: () => void;
}

const ManageBaselinesModal = ({
  open,
  tier,
  onClose,
}: ManageBaselinesModalProps) => {
  const baselinesService = appInject<IBaselinesService>(
    DI_TOKENS.baselinesService,
  );
  const [viewMode, setViewMode] = useState<"positive" | "negative">("positive");

  // List all logical baselines (Global)
  const { data: baselines = [], isLoading } = useQuery(
    ["baselines-global"],
    () => baselinesService.list(),
    { enabled: open, keepPreviousData: true },
  );

  // Derived Logic
  const sortedForNeutral = [...baselines].sort(
    (a, b) => a.draftGrade - b.draftGrade,
  );
  const NEUTRAL_GRADE = 0;

  // We want to show filtered list similar to Baselines page
  const filteredBaselines = sortedForNeutral
    .filter((b) =>
      viewMode === "positive"
        ? b.draftGrade >= NEUTRAL_GRADE
        : b.draftGrade < NEUTRAL_GRADE,
    )
    .sort((a, b) =>
      viewMode === "positive"
        ? a.draftGrade - b.draftGrade
        : b.draftGrade - a.draftGrade,
    );

  const columns = [
    {
      title: "Required Score",
      key: "calculated",
      render: (_: any, record: BaselineDto) => {
        if (!tier) return "-";
        const score = record.draftGrade + tier.gloThreshold;
        return <Space>{score}</Space>;
      },
    },
    {
      title: "Percentile",
      dataIndex: "percentile",
      render: (val: number) => `${val.toFixed(2)}%`,
    },
    {
      title: "GLO Change",
      dataIndex: "gloChange",
      render: (val: number) => (
        <span
          style={{
            color: val > 0 ? "green" : val < 0 ? "red" : "black",
            fontWeight: "bold",
          }}
        >
          {val > 0 ? `+${val}` : val}
        </span>
      ),
    },
  ];

  return (
    <Modal
      open={open}
      title={null} // Custom title in Row
      onCancel={onClose}
      footer={null}
      width={800}
    >
      <Row
        justify="space-between"
        align="middle"
        style={{ marginRight: 30, marginBottom: 10 }}
      >
        <Col>
          <Typography.Title
            level={4}
            style={{
              margin: 0,
            }}
          >
            Calculated Baselines for {tier?.name || "Tier"}
          </Typography.Title>
        </Col>
        <Col>
          <Select
            value={viewMode}
            onChange={(val) => setViewMode(val)}
            style={{ width: 220 }}
          >
            <Select.Option value="positive">Positives</Select.Option>
            <Select.Option value="negative">Negatives</Select.Option>
          </Select>
        </Col>
      </Row>

      <Typography.Text
        type="secondary"
        style={{ display: "block", marginBottom: 16 }}
      >
        These are the independent global baselines. The "Required Score" shows
        what a user in this Tier needs to score to achieve the corresponding
        Draft Grade effect.
      </Typography.Text>

      <Table
        rowKey="id"
        dataSource={filteredBaselines}
        columns={columns}
        loading={isLoading}
        pagination={false}
        scroll={{ y: 400 }}
        size="small"
      />
    </Modal>
  );
};

export default ManageBaselinesModal;
